import {Trans, useTranslation, TKey} from '@/translations/i18n.tsx'
import {Assignment, ServiceType} from '@features/assignment/types.ts'
import {differenceInDays, differenceInHours} from 'date-fns'
import {parseDate} from '@/dates.ts'
import {Button} from '@components/ui/button.tsx'
import {CheckIcon, XCloseIcon} from '@components/ui/icon.tsx'
import {useAcceptAssignment} from '@features/assignment/queries/useAcceptAssignment.ts'
import {Spinner} from '@components/ui/spinner.tsx'
import {useState} from 'react'
import {DeclineAssignmentModal} from '@features/assignment/components/decline-assignment-modal'
import {useNavigate} from 'react-router'
import {routes} from '@utilities/constants/routes.tsx'
import {formatHostAvailabilityDates, sortHostAvailabilityDates} from '@features/assignment/utils.tsx'
import {
    ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_WRAPPER_CLASSNAME
} from '@features/assignment/constants'

const getExpirationDifference = (expirationDate: string): {unit: 'day' | 'hour'; value: number} => {
    const expirationDifferenceInHours = differenceInHours(parseDate(expirationDate), new Date())
    const expirationDifferenceInDays = differenceInDays(parseDate(expirationDate), new Date())

    if (expirationDifferenceInHours > 24) {
        return {
            unit: 'day',
            value: expirationDifferenceInDays >= 0 ? expirationDifferenceInDays : 0
        }
    }

    return {
        unit: 'hour',
        value: expirationDifferenceInHours >= 0 ? expirationDifferenceInHours : 0
    }
}

export const PendingDescription = ({assignment, serviceType}: {assignment: Assignment; serviceType: ServiceType}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false)
    const acceptAssignmentMutation = useAcceptAssignment(assignment)
    const expirationDifference = assignment.expiration_date ? getExpirationDifference(assignment.expiration_date) : null

    return (
        <div className={ASSIGNMENT_DESCRIPTION_WRAPPER_CLASSNAME}>
            <div className={ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME}>
                <h3 className={ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME}>{t('assignment:make_a_decision')}</h3>
                <p className={ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME}>
                    <Trans
                        i18nKey={
                            assignment.is_bulk_assignment
                                ? 'assignment:descriptions:pending:paragraph_bulk'
                                : (`assignment:descriptions:pending:paragraph:${serviceType}` as TKey)
                        }
                        components={{strong: <strong />}}
                        values={{
                            difference: t(
                                expirationDifference?.unit === 'day' ? 'commons:x_days' : 'assignment:x_hours',
                                {
                                    count: expirationDifference?.value ?? 0
                                }
                            )
                        }}
                    />
                </p>
            </div>
            <div className="flex flex-col items-center gap-2.5">
                <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
                    <Button
                        variant="primarySuccess"
                        onClick={() => acceptAssignmentMutation.mutate(undefined)}
                        disabled={acceptAssignmentMutation.isPending}
                    >
                        <CheckIcon />
                        {t('commons:accept')}
                        {acceptAssignmentMutation.isPending && <Spinner size={20} />}
                    </Button>
                    <Button
                        variant="primaryDanger"
                        onClick={() => setIsDeclineModalOpen(true)}
                        disabled={acceptAssignmentMutation.isPending}
                    >
                        <XCloseIcon />
                        {t('commons:decline')}
                    </Button>
                </div>
                <div className="w-full wrap-break-word text-left text-sm text-neutral-600 md:text-center [&_b]:font-medium [&_b]:whitespace-nowrap">
                    {assignment?.quote?.host_availability_dates &&
                    assignment?.quote?.host_availability_dates?.length <= 2 ? (
                        <Trans
                            i18nKey="assignment:host_dates"
                            values={{
                                count: assignment.quote?.host_availability_dates.length ?? 0,
                                date1: formatHostAvailabilityDates(
                                    assignment.quote?.host_availability_dates?.[0]?.starting_time,
                                    assignment.quote?.host_availability_dates?.[0]?.ending_time
                                ),
                                date2: formatHostAvailabilityDates(
                                    assignment.quote?.host_availability_dates?.[1]?.starting_time,
                                    assignment.quote?.host_availability_dates?.[1]?.ending_time
                                )
                            }}
                            components={{b: <b />}}
                        />
                    ) : (
                        <div className="flex flex-col gap-2 text-left [&_li]:list-disc [&_ul]:pl-2">
                            {t('assignment:host_dates_more_two')}
                            <ul>
                                {sortHostAvailabilityDates(assignment?.quote?.host_availability_dates ?? []).map(
                                    date => (
                                        <li key={date.starting_time}>
                                            {formatHostAvailabilityDates(date.starting_time, date.ending_time)}
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
            {isDeclineModalOpen && (
                <DeclineAssignmentModal
                    assignment={assignment}
                    onClose={() => setIsDeclineModalOpen(false)}
                    onSuccess={() => navigate(routes.HOME.handle.fullPath)}
                    serviceType={serviceType}
                />
            )}
        </div>
    )
}
